import { QuickFillItem } from '../../types/domain/quick-fill.types';

/**
 * Serialize Quick Fill items to TSV (Tab-Separated Values) format
 * @param items - Array of QuickFillItem objects to serialize
 * @param columns - Column names to include in the output
 * @returns TSV string with header row and data rows
 */
export function serializeToTSV(items: QuickFillItem[], columns: string[]): string {
  // Header row
  const header = columns.join('\t');

  // Data rows
  const rows = items.map(item =>
    columns.map(col => item[col as keyof QuickFillItem] || '').join('\t')
  );

  return [header, ...rows].join('\n');
}

/**
 * Parse TSV (Tab-Separated Values) text into QuickFillItem array
 * @param tsvText - TSV formatted string with header row
 * @returns Array of QuickFillItem objects
 */
export function parseFromTSV(tsvText: string): QuickFillItem[] {
  const lines = tsvText.trim().replace(/\r\n?/g, '\n').split('\n');
  if (lines.length < 2) return []; // Need header + at least one row

  const headers = lines[0].split('\t');
  const items: QuickFillItem[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    const item: any = {};

    headers.forEach((header, index) => {
      item[header] = values[index] || '';
    });

    items.push(item as QuickFillItem);
  }

  return items;
}

/**
 * Copy text to clipboard using the Clipboard API
 * @param text - Text to copy to clipboard
 * @throws Error if clipboard API not supported or operation fails
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (!navigator.clipboard) {
    throw new Error('Clipboard API not supported');
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    throw new Error('Failed to copy to clipboard');
  }
}

/**
 * Read text from clipboard using the Clipboard API
 * @returns Text from clipboard
 * @throws Error if clipboard API not supported or operation fails
 */
export async function readFromClipboard(): Promise<string> {
  if (!navigator.clipboard) {
    throw new Error('Clipboard API not supported');
  }

  try {
    return await navigator.clipboard.readText();
  } catch (err) {
    throw new Error('Failed to read from clipboard');
  }
}
